import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import Swal from 'sweetalert2';
import idmlServiceInstance from '../../services/IdmlService';
import { startTrackingForNewContent } from '../tasks/startTracking';
import { targetsForWaiting } from '../tasks/taskUi';
import TaskFieldsSection, { emptyTaskFields } from '../tasks/TaskFieldsSection';
import { SelectLanguageComponent, flatLangOptions, groupedLangOptions } from '../subtitles/SelectLanguageComponent';

const sourceLanguageOptions = flatLangOptions.map((lang) => lang.label);

// SelectLanguageComponent defaults to pre-selecting Gujarati/English/Hindi with Gujarati and
// English pinned (isFixed, non-removable) - that's a video-subtitle-workflow default that
// doesn't apply here. Strip isFixed and pass as customOptions so target languages for a new
// IDML book start empty and every selection stays removable.
const targetLanguageCustomOptions = groupedLangOptions.map((group) => ({
    ...group,
    options: group.options.map((opt) => ({ ...opt, isFixed: false })),
}));

/**
 * "New Book" dialog, modeled on CreateTextFileDialog.js: upload once, optionally choose
 * target languages and whether to start translating them immediately (design doc §7).
 *
 * The book's .idml, its "Document Fonts" folder, and its "Links" folder are all uploaded together
 * as a single zip - the same layout InDesign's own File > Package export produces - instead of
 * three separate pickers. The server extracts the zip and matches the .idml file plus the two
 * folders by name (see IdmlService#createBook).
 *
 * waiting: set when this opens from a waiting task, as { title, languages, folderField }. It starts
 * from the task and has no Track this work. Translating at once is left out too: it needs the
 * editor open to chain from PARSED, and the task drawer doesn't open it.
 */
const CreateBookDialog = ({ open, onClose, currentPath, onFileCreated, darkMode = false, themeVariant = 'vibrant', waiting = null }) => {
    const [bookName, setBookName] = useState(waiting?.title || '');
    const [uploadedZip, setUploadedZip] = useState(null);
    const [sourceLanguage, setSourceLanguage] = useState('English');
    const [targetLanguages, setTargetLanguages] = useState(() => targetsForWaiting(waiting?.languages, 'English', flatLangOptions));
    const [taskFields, setTaskFields] = useState(emptyTaskFields());
    const [autoTranslate, setAutoTranslate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    // Milestone 7 (stored only for now, see design doc): book-level free-text guidance for
    // machine translation, applied identically to every target language. Not yet passed to the
    // T2TT AI Server - its custom-prompt request semantics aren't defined yet.
    const [translationPrompt, setTranslationPrompt] = useState('');

    const handleClose = () => {
        if (isLoading) return;
        setBookName('');
        setUploadedZip(null);
        setSourceLanguage('English');
        setTargetLanguages([]);
        setAutoTranslate(false);
        setTranslationPrompt('');
        setError(null);
        setTaskFields(emptyTaskFields());
        onClose();
    };

    const applyFile = (file) => {
        if (!file) return;
        if (!/\.zip$/i.test(file.name)) {
            setError('Only .zip files are supported');
            return;
        }
        setUploadedZip(file);
        if (!bookName.trim()) {
            setBookName(file.name.replace(/\.zip$/i, ''));
        }
        setError(null);
    };

    const handleFileUpload = (event) => {
        applyFile(event.target.files[0]);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        if (isLoading) return;
        applyFile(event.dataTransfer.files[0]);
    };

    const handleTargetLanguagesChange = (selectedLanguages) => {
        setTargetLanguages(selectedLanguages.filter((lang) => lang.label !== sourceLanguage));
    };

    const validateForm = () => {
        if (!uploadedZip) {
            setError('Please select a .zip file to upload');
            return false;
        }
        if (!bookName.trim()) {
            setError('Book name is required');
            return false;
        }
        return true;
    };

    const handleCreate = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await idmlServiceInstance.createBook(
                currentPath,
                bookName,
                uploadedZip,
                sourceLanguage,
                targetLanguages.map((lang) => lang.label),
                autoTranslate,
                translationPrompt,
            );
            if (!waiting) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Book uploaded',
                    text: 'Opening editor to track processing status...',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            }
            onFileCreated(result);
            startTrackingForNewContent('BOOK', [result?.id], taskFields);
            handleClose();
        } catch (err) {
            console.error('Error creating book:', err);
            setError(err.response?.data?.error || 'Failed to create book');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{waiting ? 'Create book' : 'New Book'}</DialogTitle>
            <DialogContent>
                {waiting?.folderField}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragOver ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        mb: 2,
                        cursor: 'pointer',
                    }}
                    onClick={() => document.getElementById('idml-file-input').click()}
                >
                    <input
                        id="idml-file-input"
                        type="file"
                        accept=".zip"
                        hidden
                        onChange={handleFileUpload}
                        disabled={isLoading}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {uploadedZip ? uploadedZip.name : 'Click or drag a .zip file here'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Should contain the .idml file and, optionally, its "Document Fonts" and "Links" folders.
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    margin="dense"
                    label="Book Name"
                    value={bookName}
                    onChange={(e) => setBookName(e.target.value)}
                    disabled={isLoading}
                    sx={{ mb: 2 }}
                />

                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                    <InputLabel shrink sx={{ position: 'relative', transform: 'none', fontSize: '0.75rem', mb: 0.5 }}>
                        Source Language
                    </InputLabel>
                    <Select
                        value={sourceLanguage}
                        onChange={(e) => setSourceLanguage(e.target.value)}
                        disabled={isLoading}
                        notched={false}
                    >
                        {sourceLanguageOptions.map((lang) => (
                            <MenuItem key={lang} value={lang}>
                                {lang}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                    <InputLabel shrink sx={{ position: 'relative', transform: 'none', fontSize: '0.75rem', mb: 0.5 }}>
                        Target Languages (optional, languages can be added later)
                    </InputLabel>
                    <SelectLanguageComponent
                        id="create-book-target-languages"
                        onChange={handleTargetLanguagesChange}
                        languageOptions={targetLanguages.map((l) => l.label)}
                        customOptions={targetLanguageCustomOptions}
                        disabled={isLoading}
                        themeVariant={themeVariant}
                        darkMode={darkMode}
                    />
                </FormControl>

                {!waiting && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={autoTranslate}
                                onChange={(e) => setAutoTranslate(e.target.checked)}
                                disabled={isLoading || targetLanguages.length === 0}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Start translating selected languages now
                                <Typography variant="caption" color="text.secondary" display="block">
                                    (otherwise, just Create, start translation later)
                                </Typography>
                            </Typography>
                        }
                    />
                )}

                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    margin="dense"
                    label="Prompt to review book text"
                    placeholder="e.g. Translate formally; keep Sanskrit terms untransliterated"
                    value={translationPrompt}
                    onChange={(e) => setTranslationPrompt(e.target.value)}
                    disabled={isLoading}
                    helperText="Applies to every target language on this book - otherwise, just upload - add languages later."
                    sx={{ mb: 2, mt: 1 }}
                />

                {isLoading && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Uploading book... this can take a few minutes for large files.
                    </Typography>
                )}
                {!waiting && (
                    <TaskFieldsSection
                        value={taskFields}
                        onChange={setTaskFields}
                        contentType="BOOK"
                        defaultLanguages={targetLanguages.map((l) => l.label)}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button onClick={handleCreate} variant="contained" color="primary" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : waiting ? 'Create and link' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateBookDialog;
